"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import Link from "next/link";
import {
  Search, Loader2, Send, ArrowRight, Globe, MessageCircle,
  Zap, BookOpen, AlertCircle, FileText, ExternalLink, ChevronDown,
} from "lucide-react";
import { apiPost } from "@/lib/api-client";

interface DemoItem {
  slug: string;
  title: string;
  url: string;
  excerpt: string;
  bodyMarkdown: string;
  tokenCount: number;
  contentType: string;
}

interface DemoResponse {
  domain: string;
  items: DemoItem[];
  totalSitemapPages: number;
  cached?: boolean;
  message?: string;
  cms?: string | null;
  isCloudflare?: boolean;
  dnsProvider?: string | null;
  deploymentPath?: string | null;
}

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  isStreaming?: boolean;
  interrupted?: boolean;
}

type Phase = "input" | "loading" | "chat" | "error-no-ai" | "error-no-content";

function renderChatMarkdown(text: string, isStreaming?: boolean): string {
  let t = text;

  if (isStreaming) {
    t = t.replace(/\[[^\]]*$/, "");
    t = t.replace(/\]\([^)]*$/, "");
  }

  let html = t
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/^#{1,6}\s+(.+)$/gm, "<strong>$1</strong>")
    .replace(/^[-*]\s+(.+)$/gm, "\u2022 $1")
    .replace(/^\d+\.\s+(.+)$/gm, "\u2022 $1")
    .replace(/^---+$/gm, "")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/`([^`]+)`/g, '<code class="bg-gray-200/60 px-1 py-0.5 rounded text-sm font-mono">$1</code>')
    .replace(
      /\[([^\]]+)\]\(([^)]+)\)/g,
      (_match: string, text: string, url: string) => {
        if (/^https?:\/\//i.test(url)) {
          return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-blue-600 underline hover:text-blue-800">${text}</a>`;
        }
        return `<strong>${text}</strong>`;
      },
    )
    .replace(/\n\n/g, "</p><p>")
    .replace(/\n/g, "<br>");

  return `<p>${html}</p>`;
}

const API_BASE =
  typeof window !== "undefined"
    ? (process.env.NEXT_PUBLIC_API_URL ?? "")
    : "";

function generateSuggestions(domain: string, items: DemoItem[]): string[] {
  const suggestions: string[] = [];
  suggestions.push(`What does ${domain.replace("www.", "")} do?`);

  const postItem = items.find((i) => i.contentType === "post");
  if (postItem) {
    suggestions.push(`Tell me about "${postItem.title}"`);
  }

  const productItem = items.find((i) => i.contentType === "product");
  if (productItem) {
    suggestions.push("What products are available?");
  } else if (!postItem) {
    suggestions.push("What topics does this site cover?");
  }

  if (suggestions.length < 3) {
    suggestions.push("Summarize the main content of this site");
  }

  return suggestions.slice(0, 3);
}

export default function DemoClient() {
  const [url, setUrl] = useState("");
  const [phase, setPhase] = useState<Phase>("input");
  const [loadingStatus, setLoadingStatus] = useState("");
  const [domain, setDomain] = useState("");
  const [items, setItems] = useState<DemoItem[]>([]);
  const [totalSitemapPages, setTotalSitemapPages] = useState(0);
  const [detectedCms, setDetectedCms] = useState<string | null>(null);
  const [dnsProvider, setDnsProvider] = useState<string | null>(null);
  const [deploymentPath, setDeploymentPath] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [pagesExpanded, setPagesExpanded] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const ctaUrl = (() => {
    const params = new URLSearchParams();
    params.set("url", `https://${domain}`);
    if (detectedCms) params.set("cms", detectedCms);
    if (dnsProvider) params.set("provider", dnsProvider);
    if (deploymentPath) params.set("path", deploymentPath);
    if (totalSitemapPages > 0) params.set("pages", String(totalSitemapPages));
    return `/onboarding/plan?${params.toString()}`;
  })();

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  async function handleCrawl(e: React.FormEvent) {
    e.preventDefault();
    if (!url.trim()) return;

    setPhase("loading");
    setError(null);

    let parsedDomain: string;
    try {
      const fullUrl = url.trim().startsWith("http") ? url.trim() : `https://${url.trim()}`;
      parsedDomain = new URL(fullUrl).hostname;
    } catch {
      parsedDomain = url.trim();
    }

    setDomain(parsedDomain);
    setLoadingStatus(`Reading ${parsedDomain}... Discovering pages...`);

    try {
      const data = await apiPost<DemoResponse>("/api/public/demo-crawl", { url: url.trim() });

      if (data.items.length === 0) {
        setPhase("error-no-content");
        return;
      }

      setLoadingStatus(
        `Found ${data.totalSitemapPages} pages. Reading ${Math.min(10, data.totalSitemapPages)}...`
      );

      await new Promise((r) => setTimeout(r, 600));

      setDomain(data.domain);
      setItems(data.items);
      setTotalSitemapPages(data.totalSitemapPages);
      setDetectedCms(data.cms ?? null);
      setDnsProvider(data.dnsProvider ?? null);
      setDeploymentPath(data.deploymentPath ?? null);
      setSuggestions(generateSuggestions(data.domain, data.items));
      setMessages([
        {
          role: "assistant",
          content: `Hi! I know all about ${data.domain}. Ask me anything — what we do, how our services work, or any details from our site.`,
        },
      ]);
      setPhase("chat");

      setTimeout(() => inputRef.current?.focus(), 100);
    } catch (err: any) {
      if (err.status === 429) {
        setError("You've reached the demo limit. Sign up for a free account to continue exploring.");
      } else {
        setError(err.message ?? "Something went wrong. Please try again.");
      }
      setPhase("input");
    }
  }

  async function sendMessage(text: string) {
    if (!text.trim() || isSending) return;

    const userMsg: ChatMessage = { role: "user", content: text.trim().slice(0, 500) };
    const history = messages
      .filter((m) => !m.isStreaming)
      .map((m) => ({ role: m.role, content: m.content }));

    setMessages((prev) => [...prev, userMsg]);
    setInputValue("");
    setIsSending(true);
    setSuggestions([]);

    const assistantMsg: ChatMessage = { role: "assistant", content: "", isStreaming: true };
    setMessages((prev) => [...prev, assistantMsg]);

    try {
      const response = await fetch(`${API_BASE}/api/public/demo-chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ domain, message: userMsg.content, history }),
      });

      if (response.status === 503) {
        setMessages((prev) => prev.slice(0, -2));
        setPhase("error-no-ai");
        setIsSending(false);
        return;
      }

      if (response.status === 429) {
        setMessages((prev) => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            role: "assistant",
            content: "You've reached the demo limit. Sign up for a free account to continue exploring.",
          };
          return updated;
        });
        setIsSending(false);
        return;
      }

      if (!response.ok) {
        throw new Error("Chat request failed");
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let fullText = "";
      let wasInterrupted = false;

      if (reader) {
        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split("\n");
            buffer = lines.pop() ?? "";

            for (const line of lines) {
              if (!line.startsWith("data: ")) continue;
              const data = line.slice(6).trim();
              if (!data) continue;

              try {
                const parsed = JSON.parse(data);
                if (parsed.error) {
                  wasInterrupted = true;
                  break;
                }
                if (parsed.text) {
                  fullText += parsed.text;
                  setMessages((prev) => {
                    const updated = [...prev];
                    updated[updated.length - 1] = {
                      role: "assistant",
                      content: fullText,
                      isStreaming: true,
                    };
                    return updated;
                  });
                }
                if (parsed.done) {
                  break;
                }
              } catch {
                // skip
              }
            }
          }
        } finally {
          reader.releaseLock();
        }
      }

      setMessages((prev) => {
        const updated = [...prev];
        updated[updated.length - 1] = {
          role: "assistant",
          content: fullText || "I couldn't generate a response. Please try again.",
          interrupted: wasInterrupted,
        };
        return updated;
      });
    } catch {
      setMessages((prev) => {
        const updated = [...prev];
        updated[updated.length - 1] = {
          role: "assistant",
          content: "Something went wrong. Please try asking again.",
        };
        return updated;
      });
    }

    setIsSending(false);
  }

  function handleChatSubmit(e: React.FormEvent) {
    e.preventDefault();
    sendMessage(inputValue);
  }

  function handleReset() {
    setPhase("input");
    setUrl("");
    setDomain("");
    setItems([]);
    setMessages([]);
    setSuggestions([]);
    setError(null);
  }

  if (phase === "input") {
    return (
      <div className="min-h-screen bg-white">
        <section className="pt-24 pb-16 px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-zinc-900 mb-4">
              Talk to any website
            </h1>
            <p className="text-lg text-zinc-600 mb-8 max-w-2xl mx-auto">
              Enter a URL. We&apos;ll read the site and let you ask questions about its content
              — powered by AI.
            </p>

            <form onSubmit={handleCrawl} className="flex gap-3 max-w-xl mx-auto">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                <input
                  type="text"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="example.com"
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-zinc-300 text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                />
              </div>
              <button
                type="submit"
                disabled={!url.trim()}
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              >
                Start
              </button>
            </form>

            {error && (
              <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 max-w-xl mx-auto">
                {error}
              </div>
            )}
          </div>
        </section>

        <section className="max-w-2xl mx-auto px-4 pb-8">
          <div className="flex items-center justify-center gap-8 text-sm text-zinc-500">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-bold">1</div>
              We read your site&apos;s content
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-bold">2</div>
              AI learns it
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-bold">3</div>
              You ask questions
            </div>
          </div>
        </section>

        <section className="max-w-xl mx-auto px-4 pb-20">
          <p className="text-center text-sm text-zinc-400">
            No signup required &middot; Free &middot; Your data is not stored
          </p>
        </section>

        <section className="max-w-4xl mx-auto px-4 pb-20">
          <h2 className="text-2xl font-bold text-zinc-900 text-center mb-8">
            Why make your content AI-ready?
          </h2>
          <div className="grid sm:grid-cols-3 gap-4">
            {[
              {
                icon: Globe,
                title: "AI discovery",
                desc: "Make your content discoverable by ChatGPT, Perplexity, and other AI models.",
              },
              {
                icon: MessageCircle,
                title: "Chat with your site",
                desc: "Let visitors ask questions and get answers grounded in your actual content.",
              },
              {
                icon: Zap,
                title: "Edge delivery",
                desc: "Serve AI-ready content at your domain with real-time updates.",
              },
            ].map((card) => (
              <div
                key={card.title}
                className="rounded-xl border border-zinc-200 p-5"
              >
                <card.icon className="w-6 h-6 text-blue-600 mb-3" />
                <h3 className="font-semibold text-zinc-900 mb-1">{card.title}</h3>
                <p className="text-sm text-zinc-600">{card.desc}</p>
              </div>
            ))}
          </div>
        </section>
      </div>
    );
  }

  if (phase === "loading") {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-zinc-700 text-lg font-medium">{loadingStatus}</p>
          <p className="text-zinc-400 text-sm mt-2">This may take a moment</p>
        </div>
      </div>
    );
  }

  if (phase === "error-no-content") {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-zinc-900 mb-2">No content found</h2>
          <p className="text-zinc-600 mb-6">
            We couldn&apos;t find readable content on this site. The site might not have a
            sitemap.xml, or the content might be loaded dynamically. Try a different URL.
          </p>
          <button
            onClick={handleReset}
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-colors"
          >
            Try another URL
          </button>
        </div>
      </div>
    );
  }

  if (phase === "error-no-ai") {
    return (
      <div className="min-h-screen bg-white">
        <section className="pt-16 pb-8 px-4">
          <div className="max-w-3xl mx-auto text-center">
            <AlertCircle className="w-10 h-10 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-zinc-900 mb-2">
              Chat is not available yet
            </h2>
            <p className="text-zinc-600 mb-6">
              We&apos;re still setting up. In the meantime, here&apos;s the content we found
              from {domain}.
            </p>
          </div>
        </section>
        <section className="max-w-4xl mx-auto px-4 pb-20">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-zinc-600">
              Found <span className="font-semibold text-zinc-900">{totalSitemapPages}</span> pages.
              Showing <span className="font-semibold text-zinc-900">{items.length}</span>.
            </p>
            <button onClick={handleReset} className="text-sm text-blue-600 hover:text-blue-700">
              Try another site
            </button>
          </div>
          <div className="space-y-3">
            {items.map((item) => (
              <div key={item.slug} className="rounded-xl border border-zinc-200 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <FileText className="w-4 h-4 text-zinc-400" />
                  <h3 className="font-medium text-zinc-900 truncate">{item.title}</h3>
                </div>
                <p className="text-sm text-zinc-600 line-clamp-2 mb-2">{item.excerpt}</p>
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-blue-600"
                >
                  <ExternalLink className="w-3 h-3" />
                  {new URL(item.url).pathname}
                </a>
              </div>
            ))}
          </div>
          {totalSitemapPages > items.length && (
            <div className="mt-8 rounded-xl border-2 border-blue-200 bg-blue-50 p-6 text-center">
              <p className="text-blue-900 font-semibold mb-2">
                This shows {items.length} of {totalSitemapPages.toLocaleString()} pages.
              </p>
              <p className="text-blue-700 text-sm mb-4">
                Make your entire site AI-ready — every page, always up to date.
              </p>
              <Link
                href={ctaUrl}
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-colors"
              >
                Make your site AI-ready <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="border-b border-zinc-200 bg-zinc-50 px-4 py-3">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Globe className="w-5 h-5 text-blue-600" />
            <div>
              <span className="font-semibold text-zinc-900">{domain}</span>
              <span className="text-sm text-zinc-500 ml-3">
                {items.length} pages loaded
                {totalSitemapPages > items.length && (
                  <> &middot; {totalSitemapPages} total on site</>
                )}
              </span>
            </div>
          </div>
          <button
            onClick={handleReset}
            className="text-sm text-zinc-500 hover:text-zinc-700 transition-colors"
          >
            Try another site
          </button>
        </div>
      </div>

      <div className="border-b border-zinc-200">
        <button
          onClick={() => setPagesExpanded(!pagesExpanded)}
          className="w-full flex items-center justify-between px-4 py-2 text-xs text-zinc-500 hover:bg-zinc-50 transition-colors"
        >
          <span>
            {pagesExpanded ? "Hide" : "Show"} pages used for this chat
          </span>
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${pagesExpanded ? "rotate-180" : ""}`} />
        </button>
        {pagesExpanded && (
          <div className="px-4 pb-3 space-y-1">
            {items.map((item, i) => (
              <a
                key={i}
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-xs py-1 px-2 rounded hover:bg-zinc-100 transition-colors group"
              >
                <span className="text-zinc-400 shrink-0">{i + 1}.</span>
                <span className="truncate text-zinc-900 group-hover:text-blue-600">
                  {item.title || item.slug}
                </span>
                <span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-500">
                  {item.contentType}
                </span>
                <ExternalLink className="w-3 h-3 shrink-0 text-zinc-400 opacity-0 group-hover:opacity-100 transition-opacity" />
              </a>
            ))}
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
          {messages.map((msg, i) => (
            <div
              key={i}
              className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                  msg.role === "user"
                    ? "bg-blue-600 text-white"
                    : "bg-zinc-100 text-zinc-900"
                }`}
              >
                {msg.role === "assistant" ? (
                  <div
                    className="text-sm leading-relaxed [&_strong]:font-semibold [&_a]:text-blue-600 [&_a]:underline [&_code]:bg-gray-200/60 [&_code]:px-1 [&_code]:rounded [&_code]:text-xs [&_code]:font-mono [&_p]:mb-2 [&_p:last-child]:mb-0"
                    dangerouslySetInnerHTML={{ __html: renderChatMarkdown(msg.content, msg.isStreaming) }}
                  />
                ) : (
                  <div className="whitespace-pre-wrap text-sm leading-relaxed">
                    {msg.content}
                  </div>
                )}
                {msg.isStreaming && (
                  <span className="inline-block w-1.5 h-4 bg-blue-600 ml-0.5 animate-pulse" />
                )}
                {msg.interrupted && (
                  <p className="text-xs text-amber-600 mt-2 italic">
                    Response was interrupted. Try asking again.
                  </p>
                )}
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
      </div>

      <div className="border-t border-zinc-200 bg-white px-4 py-4">
        <div className="max-w-3xl mx-auto">
          {suggestions.length > 0 && !isSending && (
            <div className="flex flex-wrap gap-2 mb-3">
              {suggestions.map((s) => (
                <button
                  key={s}
                  onClick={() => sendMessage(s)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-zinc-200 text-sm text-zinc-700 hover:bg-zinc-50 hover:border-zinc-300 transition-colors"
                >
                  <BookOpen className="w-3.5 h-3.5" />
                  {s}
                </button>
              ))}
            </div>
          )}

          <form onSubmit={handleChatSubmit} className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={`Ask about ${domain}...`}
              disabled={isSending}
              maxLength={500}
              className="flex-1 px-4 py-3 rounded-xl border border-zinc-300 text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm disabled:opacity-60"
            />
            <button
              type="submit"
              disabled={isSending || !inputValue.trim()}
              className="inline-flex items-center justify-center w-11 h-11 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {isSending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </button>
          </form>
          <div className="mt-3 text-center">
            <Link
              href={ctaUrl}
              className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors"
            >
              {totalSitemapPages > items.length
                ? `This demo covers ${items.length} of ${totalSitemapPages} pages \u2014 make your full site AI-ready`
                : "Like what you see? Make it permanent"}
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
